/**
 * Phase 22 Plan 22-03 — CreateClientDialog
 *
 * Modal form variant of the client create form.
 * Used by WorkOrderCreateWizard Step 1 inline "+ New Client" link.
 * Calls onCreated(client: ClientDto) after successful POST.
 *
 * T-22-14 mitigated: id is pinned from server response, never spoofed.
 */

import { type JSX, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCreateClientMutation } from '@/hooks/useClientQueries';
import { ApiError } from '@/lib/api/error';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';
import type { ClientDto } from '@/types/client';

import { ClientFormFields } from './ClientFormFields';
import { clientFormSchema, type ClientFormValues } from './clientFormSchema';

interface CreateClientDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Called with the newly created client after a successful POST. */
  readonly onCreated: (client: ClientDto) => void;
}

export function CreateClientDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateClientDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const createMutation = useCreateClientMutation();

  const form = useZodForm({
    schema: clientFormSchema,
    defaultValues: {
      name: '',
      code: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      address: '',
      gstNumber: '',
      panNumber: '',
      notes: '',
    },
  });

  function handleClose(): void {
    form.reset();
    setErrorMessage(null);
    onOpenChange(false);
  }

  const onSubmit = form.handleSubmit((values: ClientFormValues) => {
    setErrorMessage(null);
    createMutation.mutate(
      {
        name: values.name,
        code: values.code,
        ...(values.contactName && values.contactName !== '' ? { contactName: values.contactName } : {}),
        ...(values.contactEmail && values.contactEmail !== '' ? { contactEmail: values.contactEmail } : {}),
        ...(values.contactPhone && values.contactPhone !== '' ? { contactPhone: values.contactPhone } : {}),
        ...(values.address && values.address !== '' ? { address: values.address } : {}),
        ...(values.gstNumber && values.gstNumber !== '' ? { gstNumber: values.gstNumber } : {}),
        ...(values.panNumber && values.panNumber !== '' ? { panNumber: values.panNumber } : {}),
        ...(values.notes && values.notes !== '' ? { notes: values.notes } : {}),
      },
      {
        onSuccess: (client) => {
          toast.success(`Client "${client.name}" created`);
          handleClose();
          onCreated(client);
        },
        onError: (err) => {
          const msg = err instanceof ApiError ? (err.detail ?? err.message) : 'Unexpected error';
          setErrorMessage(msg);
          toast.error('Could not create client', { description: msg });
        },
      },
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Client</DialogTitle>
          <DialogDescription>
            Create a new client. Name and Code are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <ClientFormFields form={form} />

          {errorMessage !== null && (
            <div
              className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="create-client-dialog-error"
            >
              {errorMessage}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createMutation.isPending}
              data-testid="create-client-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
              data-testid="create-client-dialog-submit"
            >
              {createMutation.isPending ? 'Creating…' : 'Create Client'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
